import { ChevronDown, ChevronLeft, ChevronRight, Import, SearchLoop } from 'assets/icons';
import StyledInput from 'components/styledInput/StyledInput';
import Button from 'components/button';
import useModal from 'components/modal/useModal';
import UseContext from 'hooks/useContext';
import { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import removeAccents from 'remove-accents';
import Fuse from 'fuse.js';
import ApiDatabase from 'server';
import Usefetchdata from 'hooks/usefetchdata';
import PageForm from '../../components/pageForm/PageForm';
import PayslipManagementSubNav from '../../components/subnav/payslipManagement.subNav';
import { toast } from 'react-toastify';

const getInitialDate = (searchParams) => {
  const y = searchParams.get('y');
  const m = searchParams.get('m');
  if (y && m) {
    const d = new Date(Number(y), Number(m) - 1, 1);
    if (!isNaN(d.getTime())) return d;
  }
  return new Date();
};

const PayslipManagement = () => {
  const { userData } = Usefetchdata();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toggle } = useModal();
  const { setModalData, refreshPayslips, modalDataReturn } = UseContext();

  const [selectedDate, setSelectedDate] = useState(() => getInitialDate(searchParams));
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [countPerPage] = useState(20);

  const year = selectedDate
    ? String(selectedDate.getFullYear())
    : String(new Date().getFullYear());
  const month = selectedDate
    ? String(selectedDate.getMonth() + 1).padStart(2, '0')
    : String(new Date().getMonth() + 1).padStart(2, '0');

  const isPayslipTab = location.pathname.includes('payslips');
  const isInvoiceTab = location.pathname.includes('/invoices');
  const isHistory = location.pathname.includes('history') || location.pathname.includes('stats');
  const showControls = isPayslipTab || isInvoiceTab;

  // ── URL sync ──────────────────────────────────────────────────────
  useEffect(() => {
    const y = searchParams.get('y');
    const m = searchParams.get('m');
    if (y && m) {
      const d = new Date(Number(y), Number(m) - 1, 1);
      if (!isNaN(d.getTime()) && (String(d.getFullYear()) !== year || String(d.getMonth() + 1).padStart(2, '0') !== month)) {
        setSelectedDate(d);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (!showControls) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (next.get('y') === year && next.get('m') === month) return prev;
      next.set('y', year);
      next.set('m', month);
      return next;
    }, { replace: true });
  }, [year, month, showControls]);

  // ── Payslip state ─────────────────────────────────────────────────
  const [payslips, setPayslips] = useState([]);
  const [payslipTotalAmount, setPayslipTotalAmount] = useState(0);
  const [isWaitingPayslip, setIsWaitingPayslip] = useState(true);
  const [filteredPayslips, setFilteredPayslips] = useState([]);

  const fetchPayslips = () => {
    setIsWaitingPayslip(true);
    ApiDatabase.listPayslips(
      { year, month },
      (data) => {
        setPayslips(data.payslips || []);
        setPayslipTotalAmount(data.totalAmount || 0);
        setIsWaitingPayslip(false);
      },
      (err) => {
        if (err.name === 'CanceledError') return;
        console.log(err);
        setIsWaitingPayslip(false);
      }
    );
  };

  useEffect(() => {
    if (!isPayslipTab) return;
    fetchPayslips();
    setCurrentPage(1);
    setSelectedIds([]);
  }, [year, month, refreshPayslips, isPayslipTab]);

  useEffect(() => {
    if (!isPayslipTab) return;
    let result = payslips;
    if (statusFilter) result = result.filter((p) => p.status === statusFilter);
    if (search.length > 0) {
      const searchTerm = removeAccents(search.toLowerCase());
      const fuse = new Fuse(result, {
        keys: ['id_temp_worker_bestt', 'lastname', 'firstname', 'amount'],
        includeScore: true,
        threshold: 0.3,
        getFn: (obj, path) => {
          const value = Fuse.config.getFn(obj, path);
          if (value == null) return '';
          return removeAccents(value.toString().toLowerCase());
        },
      });
      result = fuse.search(searchTerm).map((r) => r.item);
    }
    setFilteredPayslips(result);
  }, [payslips, search, statusFilter, isPayslipTab]);

  // ── Invoice state ─────────────────────────────────────────────────
  const [invoices, setInvoices] = useState([]);
  const [invoiceTotalAmount, setInvoiceTotalAmount] = useState(0);
  const [isWaitingInvoice, setIsWaitingInvoice] = useState(true);
  const [filteredInvoices, setFilteredInvoices] = useState([]);

  const fetchInvoices = () => {
    setIsWaitingInvoice(true);
    ApiDatabase.listInvoices(
      { year, month },
      (data) => {
        setInvoices(data.invoices || []);
        setInvoiceTotalAmount(data.totalAmount || 0);
        setIsWaitingInvoice(false);
      },
      (err) => {
        if (err.name === 'CanceledError') return;
        console.log(err);
        setIsWaitingInvoice(false);
      }
    );
  };

  useEffect(() => {
    if (!isInvoiceTab) return;
    fetchInvoices();
    setCurrentPage(1);
    setSelectedIds([]);
  }, [year, month, isInvoiceTab]);

  useEffect(() => {
    if (!isInvoiceTab) return;
    let result = invoices;
    const paymentStatuses = ['paid', 'pending', 'partially_paid', 'late', 'cancelled', 'draft', 'to_import_on_pennylane'];
    if (paymentStatuses.includes(statusFilter)) {
      result = result.filter((inv) => inv.paid_status === statusFilter);
    } else if (statusFilter) {
      result = result.filter((inv) => inv.status === statusFilter);
    }
    if (search.length > 0) {
      const searchTerm = removeAccents(search.toLowerCase());
      const fuse = new Fuse(result, {
        keys: ['invoice_number', 'company_name', 'id_company_bestt'],
        includeScore: true,
        threshold: 0.3,
        getFn: (obj, path) => {
          const value = Fuse.config.getFn(obj, path);
          if (value == null) return '';
          return removeAccents(value.toString().toLowerCase());
        },
      });
      result = fuse.search(searchTerm).map((r) => r.item);
    }
    setFilteredInvoices(result);
  }, [invoices, search, statusFilter, isInvoiceTab]);

  useEffect(() => {
    const socket = ApiDatabase.getSocket();
    if (!socket) return;

    const onInvoiceDone = (data) => {
      if (data?.updatedInvoices?.length > 0) {
        setInvoices((prev) => prev.map((inv) => {
          const upd = data.updatedInvoices.find((u) => u.invoice_number === inv.invoice_number);
          if (!upd) return inv;
          return { ...inv, status: upd.status, paid_status: upd.paid_status, remaining_amount: upd.remaining_amount, amount: upd.amount };
        }));
        setInvoiceTotalAmount((prev) => {
          const delta = data.updatedInvoices.reduce((sum, u) => sum + (Number(u.amount) || 0), 0);
          const oldAmounts = data.updatedInvoices.reduce((sum, u) => {
            const existing = invoices.find((i) => i.invoice_number === u.invoice_number);
            return sum + (Number(existing?.amount) || 0);
          }, 0);
          return prev - oldAmounts + delta;
        });
      }
    };

    const onPayslipDone = (data) => {
      if (data?.updatedPayslips?.length > 0) {
        setPayslips((prev) => prev.map((p) => {
          const upd = data.updatedPayslips.find((u) => u.id_temp_worker_bestt === p.id_temp_worker_bestt);
          if (!upd) return p;
          return { ...p, status: upd.status, amount: upd.amount };
        }));
      }
    };

    socket.on('invoiceImportCompleted', onInvoiceDone);
    socket.on('payslipImportCompleted', onPayslipDone);
    return () => {
      socket.off('invoiceImportCompleted', onInvoiceDone);
      socket.off('payslipImportCompleted', onPayslipDone);
    };
  }, [isInvoiceTab, isPayslipTab, year, month]);

  useEffect(() => {
    if (!modalDataReturn) return;

    if (modalDataReturn?.action === 'invoices_deleted' && modalDataReturn.invoice_numbers) {
      setInvoices((prev) => prev.map((inv) =>
        modalDataReturn.invoice_numbers.includes(inv.invoice_number)
          ? { ...inv, status: 'not_imported', paid_status: null, remaining_amount: null }
          : inv
      ));
      setSelectedIds((prev) => prev.filter((id) => !modalDataReturn.invoice_numbers.includes(id)));
    } else if (modalDataReturn?.action === 'invoices_imported' && modalDataReturn.invoice_numbers) {
      setInvoices((prev) => prev.map((inv) =>
        modalDataReturn.invoice_numbers.includes(inv.invoice_number)
          ? { ...inv, status: 'imported', paid_status: inv.paid_status || 'to_import_on_pennylane' }
          : inv
      ));
    } else if (modalDataReturn?.action === 'payslips_deleted' && modalDataReturn.id_temp_worker_bestt) {
      setPayslips((prev) => prev.map((p) =>
        modalDataReturn.id_temp_worker_bestt.includes(p.id_temp_worker_bestt)
          ? { ...p, status: 'not_imported' }
          : p
      ));
      setSelectedIds((prev) => prev.filter((id) => !modalDataReturn.id_temp_worker_bestt.includes(id)));
    } else if (modalDataReturn?.action === 'payslips_imported' && modalDataReturn.id_temp_worker_bestt) {
      setPayslips((prev) => prev.map((p) =>
        modalDataReturn.id_temp_worker_bestt.includes(p.id_temp_worker_bestt)
          ? { ...p, status: 'imported' }
          : p
      ));
    }
  }, [modalDataReturn]);

  // ── Derived data (shared) ─────────────────────────────────────────
  const activeItems = isInvoiceTab ? filteredInvoices : filteredPayslips;
  const totalAmount = isInvoiceTab ? invoiceTotalAmount : payslipTotalAmount;
  const isWaiting = isInvoiceTab ? isWaitingInvoice : isWaitingPayslip;
  const countTotal = activeItems.length;
  const totalPages = Math.ceil(countTotal / countPerPage);
  const itemsToDisplay = activeItems.slice(
    (currentPage - 1) * countPerPage,
    currentPage * countPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter]);

  // Reset search/filter/selection when switching tabs
  useEffect(() => {
    setSearch('');
    setStatusFilter('');
    setSelectedIds([]);
    setCurrentPage(1);
  }, [isPayslipTab, isInvoiceTab]);

  // ── Dropdown ──────────────────────────────────────────────────────
  const [multiDropdownOpen, setMultiDropdownOpen] = useState(false);
  const multiDropdownRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (multiDropdownRef.current && !multiDropdownRef.current.contains(e.target))
        setMultiDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Guard ─────────────────────────────────────────────────────────
  if (userData?._id && !userData?.accessPayslip && !userData?.accessInvoice) {
    return <Navigate to="/dashboard" replace />;
  }

  // ── Payslip actions ───────────────────────────────────────────────
  const handlePayslipImport = () => {
    setMultiDropdownOpen(false);
    const hasSelection = selectedIds.length > 0;
    const target = hasSelection
      ? payslips.filter((p) => selectedIds.includes(p.id_temp_worker_bestt))
      : payslips;
    if (target.length === 0) return;
    setModalData({ payslips: target, year, month, preSelected: hasSelection });
    toggle('payslip_import_global');
  };

  const handlePayslipDelete = () => {
    setMultiDropdownOpen(false);
    const target = selectedIds.length > 0
      ? payslips.filter(
          (p) => selectedIds.includes(p.id_temp_worker_bestt) && p.status === 'imported'
        )
      : payslips.filter((p) => p.status === 'imported');
    if (target.length === 0) return;
    setModalData({ payslips: target, year, month });
    toggle('payslip_delete');
  };

  const handlePayslipExportCSV = async () => {
    const t = localStorage.getItem('xsrfToken');
    const url = `${process.env.REACT_APP_API_URL}admin/exportPayslipsCSV?year=${year}&month=${month}`;
    try {
      const res = await fetch(url, {
        credentials: 'include',
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      if (!res.ok) throw new Error('export failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `export-payslips-${year}-${month}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error('Export impossible');
    }
  };

  // ── Invoice actions ───────────────────────────────────────────────
  const handleInvoiceImport = () => {
    setMultiDropdownOpen(false);
    const hasSelection = selectedIds.length > 0;
    const list = hasSelection
      ? invoices.filter((inv) => selectedIds.includes(inv.invoice_number))
      : invoices;
    if (list.length === 0) return;
    setModalData({ invoices: list, year, month, preSelected: hasSelection });
    toggle('invoice_import_global');
  };

  const handleInvoiceDelete = () => {
    setMultiDropdownOpen(false);
    const importedSelected = selectedIds.length > 0
      ? invoices.filter(
          (inv) => selectedIds.includes(inv.invoice_number) && inv.status === 'imported'
        )
      : invoices.filter((inv) => inv.status === 'imported');
    if (importedSelected.length === 0) {
      toast.info('Aucune facture importée sélectionnée.');
      return;
    }
    setModalData({ invoices: importedSelected, year, month });
    toggle('invoice_delete');
  };

  const handleInvoiceExportCSV = async () => {
    const t = localStorage.getItem('xsrfToken');
    const url = `${process.env.REACT_APP_API_URL}admin/exportInvoicesCSV?year=${year}&month=${month}`;
    try {
      const res = await fetch(url, {
        credentials: 'include',
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      if (!res.ok) throw new Error('export failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `export-invoices-${year}-${month}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error('Export impossible');
    }
  };

  // ── Route to the right handler ────────────────────────────────────
  const handleImport = isInvoiceTab ? handleInvoiceImport : handlePayslipImport;
  const handleDelete = isInvoiceTab ? handleInvoiceDelete : handlePayslipDelete;
  const handleExportCSV = isInvoiceTab ? handleInvoiceExportCSV : handlePayslipExportCSV;

  return (
    <PageForm>
      <div className="p-3 flex flex-col w-full h-full gap-3">
        <div className="flex justify-end text-sm">
          <PayslipManagementSubNav />
        </div>
        {showControls && (
          <>
            <div className="flex items-center justify-between">
              <div className="flex gap-3 items-center">
                <div className="flex items-center gap-1 text-sm">
                  <button
                    className="flex items-center justify-center w-9 h-9 rounded-lg border hover:bg-gray-100"
                    onClick={() =>
                      setSelectedDate(
                        (prev) =>
                          new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
                      )
                    }
                  >
                    <ChevronLeft wh={16} color="#374151" />
                  </button>
                  <DatePicker
                    selected={selectedDate}
                    onChange={(date) => setSelectedDate(date)}
                    showMonthYearPicker
                    dateFormat="yyyy/MM"
                    className="w-full min-w-[110px] h-10 ft-b border rounded-lg text-center text-sm max-h-[38px]"
                    locale="fr-FR"
                  />
                  <button
                    className="flex items-center justify-center w-9 h-9 rounded-lg border hover:bg-gray-100"
                    onClick={() =>
                      setSelectedDate(
                        (prev) =>
                          new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
                      )
                    }
                  >
                    <ChevronRight wh={16} color="#374151" />
                  </button>
                </div>
                <div className="max-w-[160px]">
                  <select
                    className="w-full h-10 pl-2 pr-3 ft-b placeholder-gray-600 border rounded-lg text-sm"
                    onChange={(e) => setStatusFilter(e.target.value)}
                    value={statusFilter}
                  >
                    <option value="">Tous</option>
                    <option value="imported">Importé</option>
                    <option value="not_imported">Non importé</option>
                    {isInvoiceTab && <option value="paid">Payé</option>}
                    {isInvoiceTab && <option value="pending">En attente</option>}
                    {isInvoiceTab && <option value="partially_paid">Partiellement payé</option>}
                    {isInvoiceTab && <option value="late">En retard</option>}
                    {isInvoiceTab && <option value="cancelled">Annulé</option>}
                    {isInvoiceTab && <option value="to_import_on_pennylane">À importer sur PennyLane</option>}
                  </select>
                </div>
              </div>
              <div className="max-w-[250px] text-sm">
                <StyledInput
                  type="text"
                  placeholder="Rechercher"
                  imgFront={<SearchLoop wh={20} color="#6B7280" />}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="flex gap-3 items-center">
              <div className="border rounded py-2 px-4 text-sm font-medium bg-gray-50">
                Total : {isWaiting ? <span className="inline-block w-16 h-4 bg-gray-200 rounded animate-pulse align-middle" /> : `${totalAmount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`}
              </div>
              <div className="relative" ref={multiDropdownRef}>
                <Button
                  onClick={() => setMultiDropdownOpen(!multiDropdownOpen)}
                  color="PRIMARY"
                >
                  <div className="flex gap-1 items-center">
                    <span>Actions{selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}</span>
                    <ChevronDown wh={20} color="#FFFFFF" />
                  </div>
                </Button>
                {multiDropdownOpen && (
                  <div className="absolute border-2 border-[#F3F4F6] rounded-lg shadow-lg w-56 top-10 z-50 bg-gray-50">
                    <div
                      className="flex flex-row gap-2 items-center h-9 p-2 ft-sm cursor-pointer hover:bg-gray-100"
                      onClick={handleImport}
                    >
                      <Import wh={16} color="#374151" />
                      Importer
                    </div>
                    {isInvoiceTab && (
                      <>
                        <div className="border-b border-gray-100" />
                        <div
                          className="flex flex-row gap-2 items-center h-9 p-2 ft-sm cursor-pointer hover:bg-gray-100"
                          onClick={() => {
                            setMultiDropdownOpen(false);
                            setModalData({ year, month });
                            toggle('invoice_push_pennylane');
                          }}
                        >
                          {selectedIds.length > 0 && invoices
                            .filter((inv) => selectedIds.includes(inv.invoice_number))
                            .every((inv) => inv.paid_status && inv.paid_status !== 'to_import_on_pennylane')
                            ? 'Réimporter sur PennyLane'
                            : 'Exporter vers PennyLane'}
                        </div>
                      </>
                    )}
                    <div className="border-b border-gray-100" />
                    <div
                      className="flex flex-row gap-2 items-center h-9 p-2 ft-sm cursor-pointer hover:bg-gray-100 text-red-500"
                      onClick={handleDelete}
                    >
                      Supprimer
                    </div>
                  </div>
                )}
              </div>
              <div className="flex gap-2 items-center ml-auto">
                <Button size="SMI" color="SECONDARY" onClick={handleExportCSV}>
                  <div className="flex gap-1 items-center text-sm">Export CSV</div>
                </Button>
              </div>
            </div>
          </>
        )}
        <Outlet
          context={{
            // shared
            currentPage,
            setCurrentPage,
            totalPages,
            countPerPage,
            countTotal,
            isWaiting,
            selectedIds,
            setSelectedIds,
            year,
            month,
            // payslip-specific
            payslipsToDisplay: isPayslipTab ? itemsToDisplay : [],
            filteredPayslips,
            payslips,
            fetchPayslips,
            // invoice-specific
            invoicesToDisplay: isInvoiceTab ? itemsToDisplay : [],
            filteredInvoices,
            invoices,
            fetchInvoices,
          }}
        />
      </div>
    </PageForm>
  );
};

export default PayslipManagement;
